/**
 * Command palette — ⌘K / Ctrl+K.
 *
 * Rebuilt for R17 (Daniel 2026-07-27 audit). What it was: a search box over
 * three entity types where every result was a link, plus six hardcoded
 * shortcuts. What was wrong with that:
 *
 *   - It indexed deals, contacts and leads but not TASKS or DOCUMENTS, which
 *     are what an agent is usually hunting for ("where's the disclosure").
 *   - Every result navigated. Nothing ran. So the palette was a faster way to
 *     reach a page, not a way to get something done.
 *   - Opening it cold showed a fixed list of six links with no memory of what
 *     you actually work on.
 *
 * The point of a palette in a 40-destination app is that you stop navigating
 * and start typing. That only pays off if it can DO things, so:
 *
 *   • Results are grouped and typed, including tasks and documents.
 *   • Tasks complete in place — Enter opens the deal, ⌘Enter ticks the box
 *     without leaving the page you're on.
 *   • Empty state shows what you touched recently, not a static menu.
 *   • Every row states its secondary action, because a shortcut nobody can
 *     see is a shortcut nobody uses.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../auth/api';
import { updateTask } from '../api/tasks';
import { Icon, type IconName } from './Icon';
import { ModalPortal } from './ModalPortal';

interface SearchResult {
  kind: 'transaction' | 'contact' | 'lead' | 'task' | 'document';
  id: number;
  title: string;
  subtitle: string | null;
  href: string;
  transaction_id?: number | null;
  status?: string | null;
  due_date?: number | null;
}

const KIND_ICON: Record<SearchResult['kind'], IconName> = {
  transaction: 'briefcase',
  contact: 'phone',
  lead: 'users',
  task: 'list-checks',
  document: 'file-text',
};

/** Plural group headings, in the order they appear. */
const KIND_GROUP: Array<{ kind: SearchResult['kind']; label: string }> = [
  { kind: 'transaction', label: 'Deals' },
  { kind: 'task', label: 'Tasks' },
  { kind: 'contact', label: 'Contacts' },
  { kind: 'lead', label: 'Leads' },
  { kind: 'document', label: 'Documents' },
];

interface QuickAction {
  title: string;
  subtitle: string;
  href: string;
  icon: IconName;
}

const QUICK_ACTIONS: QuickAction[] = [
  { title: 'New lead', subtitle: 'Add to the pipeline', href: '/leads/new', icon: 'plus' },
  { title: 'Log my numbers', subtitle: 'Today’s activity', href: '/production', icon: 'trending-up' },
  { title: 'New transaction', subtitle: 'Start a deal from scratch', href: '/transactions/new', icon: 'briefcase' },
  { title: 'New contact', subtitle: 'Add a person', href: '/contacts/new', icon: 'phone' },
  { title: 'Submit client', subtitle: 'File a contract or agreement', href: '/submit', icon: 'file-text' },
  { title: 'Calendar', subtitle: 'Upcoming dates', href: '/calendar', icon: 'calendar' },
];


/*
 * ── Destinations ──────────────────────────────────────────────────────
 *
 * Places, matched against what you type. Distinct from QUICK_ACTIONS,
 * which only appear on an empty box.
 *
 * This list exists because of step 5. Collapsing eleven marketing links
 * into five is only an improvement if the things inside those five are
 * still findable by name — otherwise "where did Review graphics go" has no
 * answer and the tidy-up made the product worse. So every tab is
 * addressable here, with `keywords` carrying the OLD names: someone who
 * types "listing graphics" or "voice profile" lands where it moved to.
 *
 * T4 (Daniel 2026-08-04) extends the same rule past Marketing. Admin →
 * Automations holds four tabs and one of them, Smart plan rules, renders
 * nowhere else in the product. It had no route, no param and no entry here,
 * so the honest description of it was "unfindable" — the owner went looking
 * for a page he had asked for and gave up. Anything that only exists as a tab
 * belongs in this list, or it does not really exist.
 */
interface Destination {
  title: string;
  subtitle: string;
  href: string;
  icon: IconName;
  /** Extra search terms, including former names and menu labels. */
  keywords: string;
}

const DESTINATIONS: Destination[] = [
  // Marketing
  { title: 'Marketing studio', subtitle: 'Setup, plan against reality', href: '/marketing', icon: 'sparkles', keywords: 'marketing assistant hub overview gap report' },
  { title: 'Create a week of content', subtitle: 'Marketing · Create', href: '/marketing/create?t=batch', icon: 'sparkles', keywords: 'content creator generate batch posts reels avatars claude' },
  { title: 'Single post', subtitle: 'Marketing · Create', href: '/marketing/create?t=post', icon: 'pencil', keywords: 'one post generate write facebook instagram' },
  { title: 'Listing video script', subtitle: 'Marketing · Create', href: '/marketing/create?t=video', icon: 'file-text', keywords: 'video reel script walkthrough listing' },
  { title: 'Hook library', subtitle: 'Marketing · Create', href: '/marketing/create?t=hooks', icon: 'list-checks', keywords: 'hooks openers ctas transitions' },
  { title: 'Blog', subtitle: 'Marketing · Create', href: '/marketing/create?t=blog', icon: 'file-text', keywords: 'blog studio article website post cms' },
  { title: 'Newsletter', subtitle: 'Marketing · Create', href: '/marketing/create?t=newsletter', icon: 'mail', keywords: 'newsletter builder issue email monthly' },
  { title: 'Email blasts', subtitle: 'Marketing · Create', href: '/marketing/create?t=blasts', icon: 'mail', keywords: 'email blast mailjet campaign send database' },
  { title: 'Content calendar', subtitle: 'Marketing · Calendar', href: '/marketing/calendar?t=week', icon: 'calendar', keywords: 'calendar week schedule slots posting days generate' },
  { title: 'Campaigns', subtitle: 'Marketing · Calendar', href: '/marketing/calendar?t=campaigns', icon: 'calendar', keywords: 'campaign multi week launch sequence' },
  { title: 'My voice profile', subtitle: 'Marketing · Brand', href: '/marketing/brand?t=my-voice', icon: 'user', keywords: 'voice profile how i sound tone samples writing style' },
  { title: 'TurnKey brand voice', subtitle: 'Marketing · Brand', href: '/marketing/brand?t=turnkey-voice', icon: 'shield-check', keywords: 'company voice brand compliance banned words team tone' },
  { title: 'Who I write for', subtitle: 'Marketing · Brand', href: '/marketing/brand?t=avatars', icon: 'users', keywords: 'avatars personas client avatar ideal client audience' },
  { title: 'Content pillars', subtitle: 'Marketing · Brand', href: '/marketing/brand?t=pillars', icon: 'layers', keywords: 'pillars cadence topics strategy mix subjects' },
  { title: 'Buyer audiences', subtitle: 'Marketing · Brand', href: '/marketing/brand?t=audiences', icon: 'users', keywords: 'buyer audiences targeting segments' },
  { title: 'My reviews', subtitle: 'Marketing · Brand', href: '/marketing/brand?t=reviews', icon: 'message-square', keywords: 'reviews testimonials google business profile zillow stars client feedback add a review' },
  { title: 'Make a graphic', subtitle: 'Marketing · Graphics', href: '/marketing/graphics?t=make', icon: 'building-2', keywords: 'graphics listing flyer image just listed just sold open house price adjusted coming soon' },
  { title: 'Buyer side graphic', subtitle: 'Marketing · Graphics', href: '/marketing/graphics?t=make', icon: 'building-2', keywords: 'buyer side upload photos happy buyers welcome home closed keys my own photos' },
  { title: 'Review graphic', subtitle: 'Marketing · Graphics', href: '/marketing/graphics?t=make', icon: 'message-square', keywords: 'graphics review testimonial client quote google' },
  { title: 'Weekly slides', subtitle: 'Marketing · Graphics', href: '/marketing/graphics?t=slides', icon: 'sparkles', keywords: 'graphics social quote image post carousel batch slides market stats' },

  // Tools
  { title: 'Consult guides', subtitle: 'Tools', href: '/tools/consult-guides', icon: 'file-text', keywords: 'consult packet buyer seller guide appointment presentation' },
  { title: 'Document packets', subtitle: 'Tools', href: '/tools/document-packets', icon: 'file-text', keywords: 'document packet bundle disclosures' },
  { title: 'Deal analyzer', subtitle: 'Tools', href: '/investor-analyzer', icon: 'trending-up', keywords: 'investor analyzer deal analysis cash flow rental' },

  // Automations. Every tab of Admin → Automations, same rule as Marketing:
  // keywords carry the words somebody would actually type, including the
  // label the tab used to have.
  { title: 'Auto-text new leads', subtitle: 'Admin · Automations', href: '/admin/automations?t=auto_text', icon: 'message-square', keywords: 'automations auto text sms new lead five minute response quo autoresponder' },
  { title: 'Lead routing', subtitle: 'Settings · Lead capture', href: '/admin/lead-routing', icon: 'users', keywords: 'lead routing round robin jump ball cascade assign who gets leads rotation source' },
  { title: 'Lead emails', subtitle: 'Settings · Lead capture', href: '/integrations/lead-email', icon: 'mail', keywords: 'lead emails zillow realtor homes.com portal leads@ forwarding parser unparsed' },
  { title: 'Missed-call text', subtitle: 'Admin · Automations', href: '/admin/automations?t=missed_call', icon: 'phone', keywords: 'automations missed call text back sms quo voicemail callback' },
  { title: 'Birthday email', subtitle: 'Admin · Automations', href: '/admin/automations?t=birthday', icon: 'mail', keywords: 'automations birthday email greeting cron template' },
  { title: 'Home-anniversary email', subtitle: 'Admin · Automations', href: '/admin/automations?t=home_anniversary', icon: 'mail', keywords: 'automations home anniversary closing anniversary years in your home email template' },
  { title: 'Smart plan rules', subtitle: 'Admin · Automations', href: '/admin/automations?t=smart_plans', icon: 'settings', keywords: 'automations smart plans rules smart plan settings team wide plan defaults deal triggers closing trigger touch cap quiet hours timezone blackout dates plan settings' },
  // W-9 (Daniel 2026-08-11): the report import is a weekly Monday job, so it
  // needs to be findable by the words somebody types on a Monday.
  { title: 'Showing report import', subtitle: 'Admin · Automations', href: '/admin/automations?t=showing_report', icon: 'calendar', keywords: 'brokerbay showing activity report import pdf upload showings feedback buyer agent feedback weekly showings count' },

  // Elsewhere people hunt for
  { title: 'Smart plans', subtitle: 'Grow', href: '/plans', icon: 'sparkles', keywords: 'smart plan drip campaign automation follow up sequence' },
  { title: 'Starter plan library', subtitle: 'Grow · Smart plans', href: '/plans?t=library', icon: 'layers', keywords: 'starter templates plan library duplicate template sample plans' },
  { title: 'Social publishing', subtitle: 'Settings', href: '/settings/social-publishing', icon: 'arrow-up-right', keywords: 'metricool publishing connect social accounts' },
];

// ── Recents ───────────────────────────────────────────────────────────
// A palette that opens to the same six links every time never learns what
// you work on. Recents are local to the browser on purpose — they're a
// convenience, not data worth a round trip.
const RECENTS_KEY = 'tr_palette_recents_v1';
const MAX_RECENTS = 6;

type Recent = Pick<SearchResult, 'kind' | 'id' | 'title' | 'subtitle' | 'href'>;

function readRecents(): Recent[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(0, MAX_RECENTS) : [];
  } catch {
    return [];
  }
}

function pushRecent(r: Recent): void {
  try {
    const list = readRecents().filter((x) => !(x.kind === r.kind && x.id === r.id));
    list.unshift(r);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, MAX_RECENTS)));
  } catch {
    /* private mode — recents are a nicety, never break the palette for them */
  }
}

/** A row in the list: either a navigable shortcut or a search hit. */
type Item =
  | { kind: 'action'; title: string; subtitle: string; href: string; icon: IconName }
  | { kind: 'result'; result: SearchResult; group: string | null };

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [recents, setRecents] = useState<Recent[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const completeTask = useMutation({
    mutationFn: (id: number) => updateTask(id, { status: 'completed' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-tasks'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['transaction'] });
    },
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    // The sidebar's search button opens it too, so the shortcut is
    // discoverable for anyone who never learns it.
    function onOpenRequest() { setOpen(true); }
    window.addEventListener('keydown', onKey);
    window.addEventListener('tr:open-command-palette', onOpenRequest);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('tr:open-command-palette', onOpenRequest);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setQ('');
      setResults([]);
      setSelectedIdx(0);
      setToast(null);
    } else {
      setRecents(readRecents());
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search.
  useEffect(() => {
    if (!open || q.trim().length === 0) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      apiFetch<{ results: SearchResult[] }>(`/api/search?q=${encodeURIComponent(q.trim())}`)
        .then((r) => { setResults(r.results); setSelectedIdx(0); })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 150);
    return () => clearTimeout(handle);
  }, [q, open]);

  /** Flat list with group headings attached to the first row of each group,
   *  so arrow-key indexing stays simple while the UI still looks sectioned. */
  const items: Item[] = useMemo(() => {
    if (q.trim().length === 0) {
      const rec: Item[] = recents.map((r, i) => ({
        kind: 'result' as const,
        result: r as SearchResult,
        group: i === 0 ? 'Recent' : null,
      }));
      const acts: Item[] = QUICK_ACTIONS.map((a) => ({ kind: 'action' as const, ...a }));
      return [...rec, ...acts];
    }
    const out: Item[] = [];
    /*
     * Destinations first, and matched locally.
     *
     * Before the search round trip returns, and above its results: when
     * somebody types "pillars" they want the page, not a contact whose
     * notes mention the word. Server hits follow underneath.
     */
    const needle = q.trim().toLowerCase();
    const hits = DESTINATIONS.filter(
      (d) => d.title.toLowerCase().includes(needle) || d.keywords.includes(needle),
    ).slice(0, 6);
    hits.forEach((d) => out.push({
      kind: 'action', title: d.title, subtitle: d.subtitle, href: d.href, icon: d.icon,
    }));

    for (const g of KIND_GROUP) {
      const inGroup = results.filter((r) => r.kind === g.kind);
      inGroup.forEach((r, i) => {
        out.push({ kind: 'result', result: r, group: i === 0 ? g.label : null });
      });
    }
    return out;
  }, [q, results, recents]);

  /** Keep the highlighted row on screen when arrowing past the fold. */
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${selectedIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIdx]);

  const go = useCallback((item: Item) => {
    if (item.kind === 'action') {
      navigate(item.href);
      setOpen(false);
      return;
    }
    const r = item.result;
    pushRecent({ kind: r.kind, id: r.id, title: r.title, subtitle: r.subtitle, href: r.href });
    navigate(r.href);
    setOpen(false);
  }, [navigate]);

  /** ⌘Enter. The whole point of the palette: do the thing without leaving. */
  const runSecondary = useCallback((item: Item) => {
    if (item.kind !== 'result') return;
    const r = item.result;
    if (r.kind === 'task') {
      completeTask.mutate(r.id);
      setToast(`Completed “${r.title}”`);
      // Drop it from the visible list so the palette reflects reality.
      setResults((prev) => prev.filter((x) => !(x.kind === 'task' && x.id === r.id)));
      return;
    }
    // Everything else: jump to the parent deal when there is one.
    if (r.transaction_id) {
      navigate(`/transactions/${r.transaction_id}`);
      setOpen(false);
    }
  }, [completeTask, navigate]);

  /** Label for the ⌘Enter hint on the highlighted row. */
  function secondaryLabel(item: Item | undefined): string | null {
    if (!item || item.kind !== 'result') return null;
    if (item.result.kind === 'task') return 'complete it';
    if (item.result.transaction_id) return 'open the deal';
    return null;
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[selectedIdx];
      if (!item) return;
      if (e.metaKey || e.ctrlKey) runSecondary(item);
      else go(item);
    }
  }

  if (!open) return null;

  const activeSecondary = secondaryLabel(items[selectedIdx]);

  return (
    <ModalPortal>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onClick={() => setOpen(false)}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(10, 10, 10, 0.5)',
          backdropFilter: 'blur(6px)',
          zIndex: 200,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: '12vh',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            maxWidth: 620,
            width: '92%',
            maxHeight: '70vh',
            boxShadow: 'var(--shadow-lg)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '14px 18px',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <span style={{ color: 'var(--text-muted)' }}>
              <Icon name="search" size={16} />
            </span>
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Search deals, tasks, contacts, documents…"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                color: 'var(--text-heading)',
                fontSize: 15,
                fontFamily: 'inherit',
              }}
            />
            <Kbd>esc</Kbd>
          </div>

          {toast && (
            <div
              role="status"
              style={{
                padding: '8px 18px',
                fontSize: 12.5,
                color: 'var(--success)',
                background: 'var(--success-soft)',
                borderBottom: '1px solid var(--border)',
              }}
            >
              {toast}
            </div>
          )}

          <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
            {loading && (
              <div style={{ padding: 16, fontSize: 13, color: 'var(--text-muted)' }}>Searching…</div>
            )}
            {!loading && q.trim().length > 0 && items.length === 0 && (
              <div style={{ padding: 16, fontSize: 13, color: 'var(--text-muted)' }}>
                Nothing matches “{q.trim()}”.
              </div>
            )}

            {items.map((item, i) => {
              const selected = i === selectedIdx;
              const isAction = item.kind === 'action';
              const icon = isAction ? item.icon : KIND_ICON[item.result.kind];
              const title = isAction ? item.title : item.result.title;
              const subtitle = isAction ? item.subtitle : item.result.subtitle;
              const group = isAction ? null : item.group;
              const key = isAction
                ? `a-${item.title}-${i}`
                : `r-${item.result.kind}-${item.result.id}-${i}`;

              return (
                <div key={key}>
                  {group && (
                    <div
                      style={{
                        fontSize: 10,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        fontWeight: 600,
                        color: 'var(--text-muted)',
                        padding: '10px 10px 4px',
                      }}
                    >
                      {group}
                    </div>
                  )}
                  {/* First quick action after the recents gets a divider. */}
                  {isAction && i > 0 && items[i - 1]?.kind === 'result' && (
                    <div
                      style={{
                        fontSize: 10,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        fontWeight: 600,
                        color: 'var(--text-muted)',
                        padding: '10px 10px 4px',
                        borderTop: '1px solid var(--border)',
                        marginTop: 6,
                      }}
                    >
                      Jump to
                    </div>
                  )}
                  <button
                    data-idx={i}
                    onClick={() => go(item)}
                    onMouseEnter={() => setSelectedIdx(i)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      width: '100%',
                      minHeight: 44,
                      padding: '8px 10px',
                      border: 'none',
                      borderRadius: 'var(--radius)',
                      background: selected ? 'var(--bg-surface-2)' : 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: 'inherit',
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: 7,
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                        background: selected ? 'var(--brand-primary)' : 'var(--bg-surface-2)',
                        color: selected ? 'var(--text-on-brand)' : 'var(--text-muted)',
                        border: selected ? 'none' : '1px solid var(--border)',
                      }}
                    >
                      <Icon name={icon} size={13} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          display: 'block',
                          fontSize: 13.5,
                          fontWeight: 500,
                          color: 'var(--text-heading)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {title}
                      </span>
                      {subtitle && (
                        <span
                          style={{
                            display: 'block',
                            fontSize: 12,
                            color: 'var(--text-muted)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {subtitle}
                        </span>
                      )}
                    </span>
                    {/* State the secondary action on the row itself. A
                        shortcut nobody can see is a shortcut nobody uses. */}
                    {selected && secondaryLabel(item) && (
                      <span
                        onClick={(e) => { e.stopPropagation(); runSecondary(item); }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          fontSize: 11,
                          color: 'var(--text-muted)',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        <Kbd>⌘⏎</Kbd>
                        {secondaryLabel(item)}
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '9px 18px',
              borderTop: '1px solid var(--border)',
              background: 'var(--bg-surface-2)',
              fontSize: 11,
              color: 'var(--text-muted)',
            }}
          >
            <span><Kbd>↑↓</Kbd> navigate</span>
            <span><Kbd>⏎</Kbd> open</span>
            {activeSecondary && <span><Kbd>⌘⏎</Kbd> {activeSecondary}</span>}
            <span style={{ marginLeft: 'auto' }}><Kbd>⌘K</Kbd> anywhere</span>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd
      style={{
        fontSize: 10,
        fontWeight: 600,
        color: 'var(--text-body)',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-strong)',
        borderBottomWidth: 2,
        padding: '2px 5px',
        borderRadius: 4,
        fontFamily: 'ui-monospace, monospace',
        marginRight: 3,
      }}
    >
      {children}
    </kbd>
  );
}
